import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { navLinks } from '../constants';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ThemeToggle from './ThemeToggle';

const Header: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const location = useLocation();
    const isAdsLanding = location.pathname === '/exhibition-stand-quote';

    useEffect(() => { setIsOpen(false); setOpenDropdown(null); }, [location.pathname]);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Mobile: solid background, no backdrop-blur. iOS Safari can drop a fixed
    // header that carries backdrop-filter during momentum scroll, which made the
    // hamburger unreachable mid-page. Blur stays desktop-only. No transform
    // on the header: the mobile menu overlay is a fixed element inside it.
    const headerClasses = scrolled
        ? 'bg-fann-charcoal border-b border-white/5 py-4 lg:bg-fann-charcoal/90 lg:backdrop-blur-md'
        : 'bg-transparent py-6';

    return (
        <header 
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isAdsLanding ? 'ads-landing-header bg-[#111] border-b border-white/10 py-4' : headerClasses}`}
        >
            <div className="container mx-auto flex items-center justify-between">
                {/* LOGO */}
                <Link to="/" className="flex items-center z-50 hover-trigger group">
                    <span className={isAdsLanding ? "font-serif font-bold text-4xl tracking-[0.2em] text-[#f5e8bf] drop-shadow-sm" : "font-serif font-bold text-4xl tracking-[0.2em] fann-brand-wordmark drop-shadow-sm group-hover:drop-shadow-[0_0_15px_rgba(201,169,98,0.6)] transition-all duration-500"}>
                        FANN
                    </span>
                </Link>

                {/* DESKTOP NAVIGATION */}
                <nav className="hidden lg:flex items-center space-x-6 xl:space-x-10">
                    {navLinks.map((link) => (
                        <div key={link.name} className={`relative group h-full ${link.name === 'FANN Studio' ? 'lg:border-l lg:border-white/20 lg:pl-4 xl:pl-8' : ''}`}>
                            {link.children ? (
                                <button 
                                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-gray-300 hover:text-fann-gold transition-colors py-2 hover-trigger"
                                >
                                    {link.name}
                                    <ChevronDown size={10} className="opacity-70" />
                                </button>
                            ) : (
                                <NavLink
                                    to={link.path!}
                                    className={({ isActive }) => `text-xs font-semibold uppercase tracking-widest transition-colors relative link-underline hover-trigger ${
                                        isActive ? 'text-fann-gold' : 'text-gray-300 hover:text-white'
                                    }`}
                                >
                                    {link.name}
                                </NavLink>
                            )}
                            
                            {/* Dropdown */}
                            {link.children && (
                                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                                    <div className="bg-fann-charcoal border border-white/10 p-4 min-w-[200px] shadow-2xl rounded-sm">
                                        {link.children.map(child => (
                                            <NavLink
                                                key={child.name}
                                                to={child.path}
                                                className="block px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-400 hover:text-fann-gold hover:bg-white/5 transition-colors hover-trigger"
                                            >
                                                {child.name}
                                            </NavLink>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                {/* RIGHT ACTIONS */}
                <div className="hidden lg:flex items-center gap-4 xl:gap-6">
                    <ThemeToggle />
                    <a href="tel:+971505667502" dir="ltr" className="text-xs font-semibold text-fann-gold hover:text-white transition-colors whitespace-nowrap" aria-label="Call FANN at +971 50 566 7502">+971 50 566 7502</a>
                    <Link to="/contact" className="hover-trigger">
                      <button 
                          className="btn-gold"
                      >
                          Start Project
                      </button>
                    </Link>
                </div>

                {/* MOBILE TOGGLE */}
                <div className="lg:hidden flex items-center gap-4 z-50">
                    <ThemeToggle />
                    <a href="tel:+971505667502" className="text-fann-gold p-2" aria-label="Call FANN at +971 50 566 7502"><span className="sr-only">+971 50 566 7502</span><span aria-hidden="true">Call</span></a>
                    <button 
                        onClick={() => setIsOpen(!isOpen)} 
                        aria-label={isOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={isOpen}
                        className="text-white p-2 hover:text-fann-gold transition-colors"
                    >
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>
            
            {/* MOBILE MENU */}
            <AnimatePresence>
            {isOpen && (
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 bg-fann-charcoal z-40 pt-24 px-6 overflow-y-auto"
                >
                    <nav className="flex flex-col space-y-6">
                        {navLinks.map((link) => (
                             <div key={link.name} className={link.name === 'FANN Studio' ? 'border-t border-white/20 pt-6' : ''}>
                                {link.children ? (
                                    <div className="space-y-4">
                                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest border-b border-white/10 pb-2">{link.name}</div>
                                        <div className="pl-4 space-y-4 border-l border-white/10">
                                            {link.children.map(child => (
                                                <NavLink
                                                    key={child.name}
                                                    to={child.path}
                                                    onClick={() => setIsOpen(false)}
                                                    className="block text-lg font-medium text-white hover:text-fann-gold"
                                                >
                                                    {child.name}
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <NavLink
                                        to={link.path!}
                                        onClick={() => setIsOpen(false)}
                                        className="block text-2xl font-serif text-white hover:text-fann-gold"
                                    >
                                        {link.name}
                                    </NavLink>
                                )}
                            </div>
                        ))}
                        <div className="pt-8 mt-8 border-t border-white/10">
                            <Link to="/contact" onClick={() => setIsOpen(false)} className="block w-full btn-gold text-center">
                                Contact Us
                            </Link>
                        </div>
                    </nav>
                </motion.div>
            )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
